/**
 * Groupe de besoin ou demi-groupe.
 *
 * La règle vit dans le back (Groupe.est_groupe_de_besoin, décision du
 * 28/09/2026) : un groupe de besoin porte sur au moins deux classes (une
 * barrette) ; un groupe d'une seule classe est un demi-groupe (6G_G1, un
 * atelier), créé par l'ENT et partagé entre plusieurs profs. Le front LIT le
 * champ servi par le serializer, il ne recalcule jamais la règle.
 *
 * Champ absent (back plus ancien que le front, le temps d'une mise en ligne) :
 * le groupe est gardé, l'écran reste celui d'avant au lieu de se vider. Seul
 * un `false` explicite écarte un groupe.
 */
export const estGroupeDeBesoin = (groupe) => Boolean(groupe) && groupe.est_groupe_de_besoin !== false;

/** Les groupes de besoin d'une liste de groupes (liste vide si ce n'est pas un tableau). */
export const filtrerGroupesDeBesoin = (groupes) => (Array.isArray(groupes) ? groupes.filter(estGroupeDeBesoin) : []);

/**
 * Demi-groupe : le back dit EXPLICITEMENT « pas un groupe de besoin ». Une
 * classe n'a pas ce champ, un groupe au champ absent non plus : rien ne change
 * pour eux.
 */
export const estDemiGroupe = (groupe) => Boolean(groupe) && groupe.est_groupe_de_besoin === false;

// Nombre d'élèves d'un groupe, quelle que soit sa forme : le détail du groupe
// (liste `eleves`), la liste des groupes du prof (`nbeleves`) ou la carte
// (`nombre_eleves`).
const nbElevesDe = (groupe) => groupe?.eleves?.length ?? groupe?.nbeleves ?? groupe?.nombre_eleves ?? 0;

/**
 * Demi-groupe encore vide (décision prof 2026-09-30) : ses profs le composent
 * eux-mêmes en cochant les élèves de sa classe. On les mène directement à la
 * page Élèves, le menu se réduit à « Changer de classe » et « Élèves », et
 * Pilotage renvoie vers cette page. Classes et groupes de besoin : jamais.
 */
export const estDemiGroupeVide = (groupe) => estDemiGroupe(groupe) && nbElevesDe(groupe) === 0;

/** Page où l'on arrive en ouvrant un groupe : « eleves » pour un demi-groupe vide, sinon « creer ». */
export const pageDArriveeGroupe = (groupe) => (estDemiGroupeVide(groupe) ? 'eleves' : 'creer');

/**
 * Membre d'un groupe de besoin passé dans une classe hors de la barrette
 * (décision fondateur 2026-10-30) : ses résultats nationaux suivent sa classe
 * du jour, que la vue du groupe ne charge pas. Sa place n'est plus dans le
 * groupe ; l'import d'affectation du délégué l'en sortira.
 * Renvoie sa classe du jour, sinon null : membre resté dans la barrette, classe
 * inconnue, demi-groupe (une seule classe, pas une barrette) ou classe (pas de
 * classes_detail).
 */
export const classeHorsBarrette = (eleve, groupe) => {
    if (!estGroupeDeBesoin(groupe) || !Array.isArray(groupe.classes_detail)) return null;
    const classe = eleve?.classe_nom;
    if (!classe || groupe.classes_detail.includes(classe)) return null;
    return classe;
};

/** La phrase qui signale ce membre, la même dans la vue du groupe et dans sa fiche. */
export const phraseHorsBarrette = (nom, classe) =>
    `${nom} a changé de classe : ${classe}, hors de la barrette. Sa place n'est plus dans ce groupe.`;
